import type { GetAssetProofRpcResponse } from "@metaplex-foundation/js";
import type {
  BurnInstructionAccounts,
  BurnInstructionArgs,
  TransferInstructionAccounts,
  TransferInstructionArgs,
} from "@metaplex-foundation/mpl-bubblegum";
import {
  createBurnInstruction,
  createTransferInstruction,
  PROGRAM_ID as BUBBLEGUM_PROGRAM_ID,
} from "@metaplex-foundation/mpl-bubblegum";
import type { ConcurrentMerkleTreeAccount, MerkleTreeProof } from "@solana/spl-account-compression";
import { MerkleTree, SPL_ACCOUNT_COMPRESSION_PROGRAM_ID, SPL_NOOP_PROGRAM_ID } from "@solana/spl-account-compression";
import type { AccountMeta } from "@solana/web3.js";
import { PublicKey } from "@solana/web3.js";

import type { CompressedNFT } from "../../models/nfts/nfts";

export const createCompressedNftTransferIx = (
  asset: CompressedNFT,
  treeAccount: ConcurrentMerkleTreeAccount,
  assetProof: GetAssetProofRpcResponse,
  toAddress: PublicKey,
  fromAddress: PublicKey,
) => {
  const treeAddress = new PublicKey(asset.compression.tree);
  const treeAuthority = treeAccount.getAuthority();
  const canopyDepth = treeAccount.getCanopyDepth();
  const leafDelegate = !!asset.ownership.delegate ? new PublicKey(asset.ownership.delegate) : fromAddress;

  verifyRootHash(asset, treeAccount, assetProof);

  // parse the list of proof addresses into a valid AccountMeta[]
  const proofPath: AccountMeta[] = assetProof.proof
    .map((node: string) => ({
      pubkey: new PublicKey(node),
      isSigner: false,
      isWritable: false,
    }))
    .slice(0, assetProof.proof.length - (!!canopyDepth ? canopyDepth : 0));

  const accounts: TransferInstructionAccounts = {
    merkleTree: treeAddress,
    treeAuthority,
    leafOwner: fromAddress,
    leafDelegate,
    newLeafOwner: toAddress,
    logWrapper: SPL_NOOP_PROGRAM_ID,
    compressionProgram: SPL_ACCOUNT_COMPRESSION_PROGRAM_ID,
    anchorRemainingAccounts: proofPath,
  };

  const transferArgs: TransferInstructionArgs = {
    root: [...new PublicKey(assetProof.root.trim()).toBytes()],
    dataHash: [...new PublicKey(asset.compression.data_hash.trim()).toBytes()],
    creatorHash: [...new PublicKey(asset.compression.creator_hash.trim()).toBytes()],
    nonce: asset.compression.leaf_id,
    index: asset.compression.leaf_id,
  };

  return createTransferInstruction(accounts, transferArgs, BUBBLEGUM_PROGRAM_ID);
};

export const createCompressedNftBurnIx = (
  asset: CompressedNFT,
  treeAccount: ConcurrentMerkleTreeAccount,
  assetProof: GetAssetProofRpcResponse,
  fromAddress: PublicKey,
) => {
  const treeAddress = new PublicKey(asset.compression.tree);
  const treeAuthority = treeAccount.getAuthority();
  const canopyDepth = treeAccount.getCanopyDepth();

  const leafDelegate = !!asset.ownership.delegate ? new PublicKey(asset.ownership.delegate) : fromAddress;

  verifyRootHash(asset, treeAccount, assetProof);

  // parse the list of proof addresses into a valid AccountMeta[]
  const proofPath: AccountMeta[] = assetProof.proof
    .map((node: string) => ({
      pubkey: new PublicKey(node),
      isSigner: false,
      isWritable: false,
    }))
    .slice(0, assetProof.proof.length - (!!canopyDepth ? canopyDepth : 0));

  const accounts: BurnInstructionAccounts = {
    treeAuthority,
    leafOwner: fromAddress,
    leafDelegate,
    merkleTree: treeAddress,
    logWrapper: SPL_NOOP_PROGRAM_ID,
    compressionProgram: SPL_ACCOUNT_COMPRESSION_PROGRAM_ID,
    anchorRemainingAccounts: proofPath,
  };

  const transferArgs: BurnInstructionArgs = {
    root: [...new PublicKey(assetProof.root.trim()).toBytes()],
    dataHash: [...new PublicKey(asset.compression.data_hash.trim()).toBytes()],
    creatorHash: [...new PublicKey(asset.compression.creator_hash.trim()).toBytes()],
    nonce: asset.compression.leaf_id,
    index: asset.compression.leaf_id,
  };

  return createBurnInstruction(accounts, transferArgs, BUBBLEGUM_PROGRAM_ID);
};

const verifyRootHash = (
  asset: CompressedNFT,
  treeAccount: ConcurrentMerkleTreeAccount,
  assetProof: GetAssetProofRpcResponse,
) => {
  /**
   * Perform client side verification of the proof that was provided by the RPC
   * ---
   * NOTE: This is not required to be performed, but may aid in catching errors
   * due to your RPC providing stale or incorrect data (often due to caching issues)
   * The actual proof validation is performed on-chain.
   */
  const merkleTreeProof: MerkleTreeProof = {
    leafIndex: asset.compression.leaf_id,
    leaf: new PublicKey(assetProof.leaf).toBuffer(),
    root: new PublicKey(assetProof.root).toBuffer(),
    proof: assetProof.proof.map((node: string) => new PublicKey(node).toBuffer()),
  };

  const currentRoot = treeAccount.getCurrentRoot();
  const rpcRoot = new PublicKey(assetProof.root).toBuffer();

  const isRpcRootValid = MerkleTree.verify(rpcRoot, merkleTreeProof, false);
  if (!isRpcRootValid) {
    console.warn("RPC provided proof/root is invalid");
  }

  const isOnchainRootEqToRpcRoot = new PublicKey(currentRoot).toBase58() === new PublicKey(rpcRoot).toBase58();
  if (!isOnchainRootEqToRpcRoot) {
    console.warn("Current on-chain root does not match RPC provided root");
  }
};
